import type { ComponentType } from "react";

type Loader<P> = () => Promise<{ default: ComponentType<P> }>;
export type PreloadableComponent<P> = ComponentType<P> & {
  preload: () => Promise<void>;
};

const allLoaders: Array<() => Promise<void>> = [];

/**
 * Like React.lazy, but:
 *   - exposes a `preload()` so SSR/prerender can resolve the module before render
 *   - renders synchronously once the module has resolved (no Suspense throw)
 *
 * Use Suspense in the consumer for the CSR case (first navigation to a route).
 */
export function lazyRoute<P extends object>(
  loader: Loader<P>,
): PreloadableComponent<P> {
  let Component: ComponentType<P> | undefined;
  let promise: Promise<void> | undefined;

  const load = () => {
    if (!promise) {
      promise = loader().then((m) => {
        Component = m.default;
      });
    }
    return promise;
  };

  const Wrapper = ((props: P) => {
    if (!Component) throw load();
    const C = Component;
    return <C {...props} />;
  }) as PreloadableComponent<P>;

  Wrapper.preload = load;
  allLoaders.push(load);
  return Wrapper;
}

export function preloadAllRoutes(): Promise<void> {
  return Promise.all(allLoaders.map((l) => l())).then(() => undefined);
}
